import React, { lazy, Suspense, useState } from 'react';
import { Button, ConfigProvider, Modal, Spin } from 'antd';
import { useStyle } from '../../../components/ui/BtnLinearBgHook';
import { motion, AnimatePresence } from 'framer-motion';

// Loaded when the modal content is first shown, not with every page: this button is in
// the navbar, so static imports put all three forms in the app's first download.
const Register = lazy(() => import('../Register'));
const Login = lazy(() => import('../Login'));
const ForgotPassword = lazy(() => import('../ForgotPassword'));

const ModalOpener: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [formType, setFormType] = useState<'login' | 'register' | 'forgotPassword'>('login');
    const { styles } = useStyle();

    const handleToggleForm = () => {
        setFormType((prevFormType) => (prevFormType === 'login' ? 'register' : 'login'));
    };

    const setForgotPassword = () => {
        setFormType('forgotPassword');
    };

    const goToLogin = () => {
        setFormType('login');
    };

    const closeModal = () => {
        setOpen(false);
        setFormType('login'); // Reset to login form when modal is closed
    };

    // Animation variants for form transitions
    const formVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" as const } },
        exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
    };

    // Get title based on form type
    const getModalTitle = () => {
        switch (formType) {
            case 'login':
                return 'Se Connecter';
            case 'register':
                return 'Créer un compte';
            case 'forgotPassword':
                return 'Récupération de mot de passe';
            default:
                return '';
        }
    };

    return (
        <>
            <ConfigProvider
                button={{
                    className: styles.linearGradientButton,
                }}
            >
                <Button
                    type="primary"
                    onClick={() => setOpen(true)}
                    size='large'
                    className="hover:scale-105 transition-transform duration-300"
                >
                    {formType === 'login' ? 'Se Connecter' : 'Inscription'}
                </Button>
            </ConfigProvider>
            <Modal
                title={
                    <div className="text-center py-2">
                        <h2 className="text-2xl font-bold primary-gradient-text">{getModalTitle()}</h2>
                        <div className="w-16 h-1 bg-gradient-to-r from-primary-default to-accent-default mx-auto mt-2 rounded-full"></div>
                    </div>
                }
                centered
                open={open}
                onCancel={closeModal}
                width={900}
                footer={null}
                className="auth-modal"
                style={{ borderRadius: '16px', overflow: 'hidden' }}
                styles={{
                    body: { padding: 0 },
                    mask: {
                        backdropFilter: 'blur(5px)',
                        background: 'rgba(0, 0, 0, 0.5)',
                    }
                }}
                mask={true}
            >
                {/* Modal content */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={formType}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={formVariants}
                    >
                        <Suspense fallback={<div className="flex justify-center py-16"><Spin size="large" /></div>}>
                        {
                            formType === 'forgotPassword' ? (
                                <ForgotPassword
                                    closeModal={closeModal}
                                    goToLogin={goToLogin}
                                />
                            ) :
                                formType === 'login' ? (
                                    <Login
                                        handleToggleForm={handleToggleForm}
                                        closeModal={closeModal}
                                        setForgotPassword={setForgotPassword}
                                    />
                                ) : (
                                    <Register
                                        handleToggleForm={handleToggleForm}
                                        closeModal={closeModal}
                                        setForgotPassword={setForgotPassword}
                                    />
                                )}
                        </Suspense>
                    </motion.div>
                </AnimatePresence>
            </Modal>
        </>
    );
};
export default ModalOpener;
